import { t } from "@lingui/core/macro";

import { invariant } from "@/common/utils/invariant";
import {
  CropCategory,
  CropSubcategory,
} from "@/generated/digitalnisklady.cz/graphql";

const subcategoryTranslations = new Map<CropSubcategory, () => string>();

subcategoryTranslations.set(CropSubcategory.Bioactivcz, () => t`BioAktivCZ`);
subcategoryTranslations.set(CropSubcategory.Calcareous, () => t`Vápenatá`);
subcategoryTranslations.set(
  CropSubcategory.CalciumMagnesiumSulphur,
  () => t`Ostatní - Ca/Mg/S`,
);
subcategoryTranslations.set(CropSubcategory.Foliar, () => t`Listová`);
subcategoryTranslations.set(CropSubcategory.Magnesium, () => t`Hořečnatá`);
subcategoryTranslations.set(
  CropSubcategory.MultiComponent,
  () => t`Vícesložková`,
);
subcategoryTranslations.set(CropSubcategory.Nitrogen, () => t`Dusíkatá`);
subcategoryTranslations.set(
  CropSubcategory.NitrogenSulphur,
  () => t`Dusíkatá se sírou`,
);
subcategoryTranslations.set(CropSubcategory.Other, () => t`Ostatní`);
subcategoryTranslations.set(CropSubcategory.Phosphate, () => t`Fosforečná`);
subcategoryTranslations.set(CropSubcategory.Potassium, () => t`Draselná`);

subcategoryTranslations.set(CropSubcategory.Fungicides, () => t`Fungicidy`);
subcategoryTranslations.set(CropSubcategory.Herbicides, () => t`Herbicidy`);
subcategoryTranslations.set(CropSubcategory.Insecticides, () => t`Insekticidy`);
subcategoryTranslations.set(
  CropSubcategory.AuxiliaryPreparations,
  () => t`Pomocné přípravky`,
);
subcategoryTranslations.set(
  CropSubcategory.GrowthRegulators,
  () => t`Regulátory růstu`,
);
subcategoryTranslations.set(CropSubcategory.Rodenticides, () => t`Rodenticidy`);
subcategoryTranslations.set(CropSubcategory.Packages, () => t`Balíčky`);

const getSubcategoryTranslation = (subcategory: CropSubcategory): string => {
  const translation = subcategoryTranslations.get(subcategory);
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(translation, `Unknown subcategory: ${subcategory}`);
  return translation();
};

const categorySubcategories = new Map<CropCategory, CropSubcategory[]>();

categorySubcategories.set(CropCategory.Fertilizer, [
  CropSubcategory.Nitrogen,
  CropSubcategory.NitrogenSulphur,
  CropSubcategory.Phosphate,
  CropSubcategory.Potassium,
  CropSubcategory.Magnesium,
  CropSubcategory.Calcareous,
  CropSubcategory.MultiComponent,
  CropSubcategory.Foliar,
  CropSubcategory.Other,
  CropSubcategory.CalciumMagnesiumSulphur,
  CropSubcategory.Bioactivcz,
]);

categorySubcategories.set(CropCategory.PlantProtection, [
  CropSubcategory.Fungicides,
  CropSubcategory.Herbicides,
  CropSubcategory.Insecticides,
  CropSubcategory.AuxiliaryPreparations,
  CropSubcategory.GrowthRegulators,
  CropSubcategory.Rodenticides,
  CropSubcategory.Packages,
]);

const hasSubcategories = (category: CropCategory): boolean =>
  categorySubcategories.has(category);

const getSubcategoryTiles = (
  category: CropCategory,
  present: ReadonlySet<string>,
): Array<{ label: string; subcategory: CropSubcategory }> => {
  const tiles = (categorySubcategories.get(category) ?? [])
    .filter((subcategory) => present.has(subcategory))
    .map((subcategory) => ({
      label: getSubcategoryTranslation(subcategory),
      subcategory,
    }));

  return category === CropCategory.Fertilizer
    ? tiles
    : tiles.toSorted((a, b) => a.label.localeCompare(b.label));
};

const categoryToSlug = (category: CropCategory): string => {
  return category.toLowerCase().replace(/_/g, "-");
};

const slugToCategory = (slug: string): CropCategory | null => {
  const enumValue = slug.toUpperCase().replace(/-/g, "_");
  if (Object.values(CropCategory).includes(enumValue as CropCategory)) {
    return enumValue as CropCategory;
  }
  return null;
};

const subcategoryToSlug = (subcategory: CropSubcategory): string => {
  return subcategory.toLowerCase().replace(/_/g, "-");
};

const slugToSubcategory = (slug: string): CropSubcategory | null => {
  const enumValue = slug.toUpperCase().replace(/-/g, "_");
  if (Object.values(CropSubcategory).includes(enumValue as CropSubcategory)) {
    return enumValue as CropSubcategory;
  }
  return null;
};

const PROMOTIONAL_OFFER_SLUG = "promotional-offer";

const isPromotionalOfferSlug = (slug: string): boolean =>
  slug === PROMOTIONAL_OFFER_SLUG;

export {
  categoryToSlug,
  getSubcategoryTiles,
  getSubcategoryTranslation,
  hasSubcategories,
  isPromotionalOfferSlug,
  PROMOTIONAL_OFFER_SLUG,
  slugToCategory,
  slugToSubcategory,
  subcategoryToSlug,
};
